import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Car, Wrench, Loader2, Zap } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/lib/auth";

const SPECIALIZATIONS = [
  "Meccanica",
  "Elettrauto",
  "Meccatronica & ECU",
  "Cambi automatici",
  "Ibrido & Elettrico",
  "Impianti GPL/Metano",
];
const VEHICLE_TYPES = [
  { id: "auto", label: "Auto" },
  { id: "furgone", label: "Furgoni" },
  { id: "camion", label: "Camion" },
];

const inputCls =
  "w-full bg-obsidian/60 border border-white/10 rounded-lg px-4 py-3 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-volt focus:ring-1 focus:ring-volt transition-colors";

const errMsg = (e) =>
  typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Operazione non riuscita. Riprova.";

export default function AuthPage() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [role, setRole] = useState(params.get("role") === "esperto" ? "esperto" : "cliente");
  const [form, setForm] = useState({ name: "", email: "", password: "", bio: "" });
  const [specs, setSpecs] = useState([]);
  const [vtypes, setVtypes] = useState([]);
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (arr, setArr, v) =>
    setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Bentornato!");
      } else {
        if (form.name.trim().length < 2) throw new Error("Inserisci il tuo nome");
        if (form.password.length < 8) throw new Error("Password di almeno 8 caratteri");
        if (role === "esperto" && (specs.length === 0 || vtypes.length === 0))
          throw new Error("Seleziona almeno una specializzazione e un tipo di veicolo");
        await register({
          name: form.name,
          email: form.email,
          password: form.password,
          role,
          specializations: specs,
          vehicle_types: vtypes,
          bio: form.bio,
        });
        toast.success(role === "esperto" ? "Profilo esperto creato!" : "Account creato!");
      }
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.message?.includes("response") ? errMsg(err) : err.message || errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian bg-grid flex items-center justify-center px-4 py-24" data-testid="auth-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="glass-card rounded-2xl p-6 sm:p-10 w-full max-w-lg"
      >
        <div className="flex justify-center mb-6">
          <Logo />
        </div>

        <div className="grid grid-cols-2 gap-2 mb-7 p-1 rounded-full border border-white/10">
          {["login", "register"].map((m) => (
            <button
              key={m}
              data-testid={`auth-tab-${m}`}
              onClick={() => setMode(m)}
              className={`py-2.5 rounded-full font-mono text-xs uppercase tracking-widest transition-colors ${
                mode === m ? "bg-volt text-obsidian font-bold" : "text-slate-500 hover:text-slate-300"
              }`}
            >
              {m === "login" ? "Accedi" : "Registrati"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-3" data-testid="auth-form">
          {mode === "register" && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-1">
                <button
                  type="button"
                  data-testid="auth-role-cliente"
                  onClick={() => setRole("cliente")}
                  className={`flex items-start gap-3 rounded-xl border p-4 text-left transition-all ${
                    role === "cliente" ? "border-volt bg-volt/10" : "border-white/10 hover:border-volt/40"
                  }`}
                >
                  <Car size={20} className={role === "cliente" ? "text-volt" : "text-slate-500"} />
                  <span>
                    <span className="block text-sm font-semibold text-white">Ho bisogno di aiuto</span>
                    <span className="block text-xs text-slate-500 mt-0.5">Officina o privato con un veicolo in difficoltà</span>
                  </span>
                </button>
                <button
                  type="button"
                  data-testid="auth-role-esperto"
                  onClick={() => setRole("esperto")}
                  className={`flex items-start gap-3 rounded-xl border p-4 text-left transition-all ${
                    role === "esperto" ? "border-spark bg-spark/10" : "border-white/10 hover:border-spark/40"
                  }`}
                >
                  <Wrench size={20} className={role === "esperto" ? "text-spark" : "text-slate-500"} />
                  <span>
                    <span className="block text-sm font-semibold text-white">Sono un esperto</span>
                    <span className="block text-xs text-slate-500 mt-0.5">Tecnico specializzato, voglio dare consulenze</span>
                  </span>
                </button>
              </div>
              <input data-testid="auth-name-input" className={inputCls} placeholder="Nome e cognome / Nome officina *" value={form.name} onChange={set("name")} />
            </>
          )}

          <input data-testid="auth-email-input" type="email" className={inputCls} placeholder="Email *" value={form.email} onChange={set("email")} />
          <input
            data-testid="auth-password-input"
            type="password"
            className={inputCls}
            placeholder={mode === "register" ? "Password (min. 8 caratteri) *" : "Password *"}
            value={form.password}
            onChange={set("password")}
          />

          {mode === "register" && role === "esperto" && (
            <>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Specializzazioni *</p>
                <div className="flex flex-wrap gap-2" data-testid="auth-specs-chips">
                  {SPECIALIZATIONS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      data-testid={`auth-spec-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() => toggle(specs, setSpecs, s)}
                      className={`px-3 py-1.5 rounded-full border text-xs transition-colors ${
                        specs.includes(s) ? "border-volt text-volt bg-volt/10" : "border-white/10 text-slate-400 hover:border-volt/40"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Veicoli che tratti *</p>
                <div className="flex flex-wrap gap-2">
                  {VEHICLE_TYPES.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      data-testid={`auth-vtype-${v.id}`}
                      onClick={() => toggle(vtypes, setVtypes, v.id)}
                      className={`px-3 py-1.5 rounded-full border text-xs transition-colors ${
                        vtypes.includes(v.id) ? "border-spark text-spark bg-spark/10" : "border-white/10 text-slate-400 hover:border-spark/40"
                      }`}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>
              <textarea
                data-testid="auth-bio-input"
                className={`${inputCls} min-h-[70px] resize-y`}
                placeholder="Breve bio: anni di esperienza, marchi trattati..."
                value={form.bio}
                onChange={set("bio")}
              />
            </>
          )}

          <button
            data-testid="auth-submit-button"
            type="submit"
            disabled={busy}
            className="w-full flex items-center justify-center gap-2 bg-volt text-obsidian font-semibold px-6 py-3.5 rounded-full hover:bg-spark hover:scale-[1.02] transition-all duration-200 disabled:opacity-50"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />}
            {mode === "login" ? "Accedi" : role === "esperto" ? "Diventa Esperto" : "Crea Account"}
          </button>
        </form>

        <div className="flex items-center gap-3 my-5">
          <div className="h-px flex-1 bg-white/10" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-slate-600">oppure</span>
          <div className="h-px flex-1 bg-white/10" />
        </div>

        <button
          type="button"
          data-testid="google-login-button"
          onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            const redirectUrl = window.location.origin + "/auth/callback";
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
          className="w-full flex items-center justify-center gap-3 border border-white/15 text-slate-200 font-semibold px-6 py-3.5 rounded-full hover:border-volt hover:text-volt transition-all duration-200"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.2-2.1 3.7-5.1 3.7-8.6z" />
            <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.2 0-5.9-2.1-6.8-5.1l-3.9 3C3.2 21.3 7.3 24 12 24z" />
            <path fill="#FBBC05" d="M5.2 14.3c-.2-.7-.4-1.5-.4-2.3s.1-1.6.4-2.3l-3.9-3C.5 8.2 0 10 0 12s.5 3.8 1.3 5.3l3.9-3z" />
            <path fill="#EA4335" d="M12 4.7c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.1 15.2 0 12 0 7.3 0 3.2 2.7 1.3 6.7l3.9 3c.9-2.9 3.6-5 6.8-5z" />
          </svg>
          Continua con Google
        </button>
        {mode === "register" && (
          <p className="text-[11px] text-slate-600 text-center mt-3">
            Con Google ti registri come cliente. Per diventare esperto usa email e password.
          </p>
        )}

        <Link to="/" data-testid="auth-back-home-link" className="block text-center mt-5 font-mono text-[10px] uppercase tracking-widest text-slate-600 hover:text-volt transition-colors">
          ← Torna alla home
        </Link>
      </motion.div>
    </div>
  );
}
